'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  ZuperSettings,
  ZuperUser,
  AuthState,
  LoginCredentials,
  ZUPER_STORAGE_KEYS,
  ZUPER_COMPANY_NAME,
  ZUPER_TEAM_UID,
} from './types';

interface ZuperContextValue {
  // Auth state
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  currentUser: ZuperUser | null;

  // Auth actions
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;

  // Backward-compatible settings object
  settings: ZuperSettings | null;

  // Refresh user
  refreshCurrentUser: () => Promise<void>;
}

const ZuperContext = createContext<ZuperContextValue | null>(null);

export function useZuper() {
  const context = useContext(ZuperContext);
  if (!context) {
    throw new Error('useZuper must be used within a ZuperProvider');
  }
  return context;
}

export function useZuperOptional() {
  return useContext(ZuperContext);
}

interface ZuperProviderProps {
  children: React.ReactNode;
}

export function ZuperProvider({ children }: ZuperProviderProps) {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [currentUser, setCurrentUser] = useState<ZuperUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load auth state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ZUPER_STORAGE_KEYS.AUTH);
      if (stored) {
        const parsed = JSON.parse(stored) as AuthState;
        if (parsed.auth_token && parsed.user) {
          setAuthState(parsed);
          setCurrentUser(parsed.user);
        }
      }
    } catch (e) {
      console.error('Failed to load Zuper auth from localStorage:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch current user to validate token and refresh user data
  const fetchCurrentUser = useCallback(async (token: string, userUid: string) => {
    try {
      const response = await fetch(`/api/zuper/user/${userUid}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        // Token expired or invalid - clear auth
        localStorage.removeItem(ZUPER_STORAGE_KEYS.AUTH);
        setAuthState(null);
        setCurrentUser(null);
        setError('Session expired. Please log in again.');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch user: ${response.status}`);
      }

      const data = await response.json();
      if (data.type === 'success' && data.data) {
        setCurrentUser(data.data);
        // Update stored user data
        if (authState) {
          const updatedAuth = { ...authState, user: data.data };
          localStorage.setItem(ZUPER_STORAGE_KEYS.AUTH, JSON.stringify(updatedAuth));
          setAuthState(updatedAuth);
        }
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch current user';
      setError(message);
      console.error('Failed to fetch current user:', e);
    }
  }, [authState]);

  // Login function
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/zuper/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_login_name: ZUPER_COMPANY_NAME,
          email: credentials.email,
          password: credentials.password,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.type === 'error') {
        const errorMessage = data.message || 'Login failed. Please check your credentials.';
        setError(errorMessage);
        return false;
      }

      // Handle response - auth_token and user can be at root level or under data
      const authToken = data.auth_token || data.data?.auth_token;
      const userData = data.user || data.data?.user;

      if (authToken && userData) {
        // Map the user data to our ZuperUser format
        const user: ZuperUser = {
          user_uid: userData.user_uid,
          first_name: userData.first_name,
          last_name: userData.last_name,
          email: userData.email,
          profile_picture: userData.profile_picture || null,
          designation: userData.designation || null,
          emp_code: userData.emp_code || null,
          home_phone_number: userData.home_phone_number || null,
          work_phone_number: userData.work_phone_number || null,
          mobile_phone_number: userData.mobile_phone_number || null,
          hourly_labor_charge: userData.hourly_labor_charge || null,
          is_active: userData.is_active ?? true,
          is_deleted: userData.is_deleted ?? false,
          created_at: userData.created_at || '',
          updated_at: userData.updated_at || '',
          last_login_at: userData.last_login_at || null,
          role: userData.role || { role_uid: '', role_name: userData.role || '', role_key: '' },
        };

        const newAuthState: AuthState = {
          auth_token: authToken,
          user: user,
        };

        localStorage.setItem(ZUPER_STORAGE_KEYS.AUTH, JSON.stringify(newAuthState));
        setAuthState(newAuthState);
        setCurrentUser(user);
        return true;
      } else {
        setError('Invalid response from login API');
        return false;
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Login failed';
      setError(message);
      console.error('Login failed:', e);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem(ZUPER_STORAGE_KEYS.AUTH);
    // Also clear legacy settings if present
    localStorage.removeItem(ZUPER_STORAGE_KEYS.SETTINGS);
    setAuthState(null);
    setCurrentUser(null);
    setError(null);
  }, []);

  // Refresh current user
  const refreshCurrentUser = useCallback(async () => {
    if (authState) {
      await fetchCurrentUser(authState.auth_token, authState.user.user_uid);
    }
  }, [authState, fetchCurrentUser]);

  // Derive backward-compatible settings object
  const settings: ZuperSettings | null = authState ? {
    team_uid: ZUPER_TEAM_UID,
    user_uid: authState.user.user_uid,
    auth_token: authState.auth_token,
  } : null;

  const value: ZuperContextValue = {
    isAuthenticated: !!authState,
    isLoading,
    error,
    currentUser,
    login,
    logout,
    settings,
    refreshCurrentUser,
  };

  return <ZuperContext.Provider value={value}>{children}</ZuperContext.Provider>;
}
